import { useCallback, useState } from 'react';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, Spin } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { BlueButton } from '@share/components';
import { ModalShare, HotelStars } from '@components';
import { DownLoadShareType, HandleDownload } from '@share/api';
import { IActivityCompare } from '@common-types';
import { formatDateCheckInOut, renderNumber, LoginType, isSpanish, RootState } from '@share/utils';
import { HasPermission, openActivitySharedPage, ViewQuotesSharePermission } from '@utils';
import { Currency } from '@components';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import { es } from 'date-fns/locale';
import './style.scss';

import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';

const modalWidth = 900;
const percents = 100;

export interface ModalCompareProps {
  visible: boolean;
  activities: IActivityCompare[];

  onCancel: () => void;
  onRemove: (activity: IActivityCompare ) => void;

}
export function ModalCompare({
  visible,
  activities,
  
  onCancel,
  onRemove, 
}: ModalCompareProps) {
  const intl = useIntl();
  const marginatorStore = useSelector((state: RootState) => state.marginatorStore);
  const activityLocationsStore = useSelector((state: RootState) => state.activityLocationsStore);
  const { account, user } = useSelector((state: RootState) => state.loginStore);
  const {sessionKey} = useSelector((state: RootState) => state.activityStore);

  const [loading, setLoading] = useState(false);
  const [visibleAction, setVisibleAction] = useState(false);
  const [isShare, setIsShare] = useState(false);
  const history = useHistory();

  const rooms= activities[0]?.roomOccupancies || [];
  const startDate = activities[0]?.startDate;

  const handleView = (activity: IActivityCompare) => {
    openActivitySharedPage(activity,history);
  };

   const handleData = useCallback(
    (members?: string[], comment?: string) => {
      const value = marginatorStore.value || 0;
      const isB2C = account?.isB2C;
      const effectiveLocation = activityLocationsStore.selectedLocation ?? activityLocationsStore.location;

      const adultsCount = rooms?.length
        ? rooms.map((room) => room.adultsCount || 0).reduce((acc, curr) => acc + curr, 0)
        : 0;
      const kids = rooms?.length ? rooms.flatMap((room) => room.kids?.map((k) => k.age || 0) || []) : [];

      const data = {
        sessionKey,
        checkIn: startDate,
        comment: comment || '',
        members,
        activityRequest: {
          location: effectiveLocation?.name || '',
          checkIn: startDate,
          adultsCount,
          kids,
        },
        quotes: activities.map((activity) => {
          const rate = activity.modalities?.[0]?.rates?.[0];
          const price = rate?.price || 0;
          const marginatorPrice = price / (1 - value / 100);
          const publicPrice =
            isB2C || !rate?.publicPrices?.length ? null : Math.min(...rate.publicPrices.map((p) => p.price));
            
          const savingsNunmber = rate.saving / marginatorPrice * 100;
          const discount = savingsNunmber ? Math.floor(savingsNunmber) : 0;

          return {
            activityId: activity.code,
            displayName: activity.name,
            description: activity.content?.description || '',
            duration: activity.modalities?.[0] ? `${activity.modalities[0].durationValue} ${activity.modalities[0].durationMetric}` : '',
            location: activity?.locationObj?.name || '',
            image: get(activity, 'content.mediaImages[0].resource') || '',
            price: marginatorPrice,
            publicPrice: publicPrice,
            currency: activity.currency,
            isRefundable: rate?.refundable || false,
            refundability: rate?.refundable ? 'refundable' : 'non-refundable',
            // cancellationPolicy: rate?.cancellationPolicies ,
            starRating: activity.reviews ? activity.reviews.averageRating / 2 : 0,
            reviewsCount: activity.reviews?.total || 0,
            discount: discount > 0 ? discount : 0,
            saving: savingsNunmber > 0 ? savingsNunmber : 0,
          };
        }),
      };
      return data;
    },
    [
      marginatorStore.value,
      account?.isB2C,
      activities,
      startDate,
      rooms,
      sessionKey,
      activityLocationsStore.selectedLocation,
      activityLocationsStore.location,
    ],
  );

    const handleDownload = () => {
      const prevLoading = loading;
      setLoading(true);
      HandleDownload(DownLoadShareType.ACTIVITY, prevLoading, handleData(), intl, () => setLoading(false), () =>
        setVisibleAction(false),
      );
  };

  const adults = rooms?.length
    ? rooms.map((room) => room.adultsCount || 0).reduce((acc, curr) => acc + curr, 0)
    : 0;
  const childs = rooms?.length ? rooms.map((room) => room.kids?.length || 0).reduce((acc, curr) => acc + curr, 0) : 0;
  const isB2C = account?.isB2C;

  const fontFamily = account?.fontFamily;
  const secondaryFontFamily = account?.secondaryFontFamily;
  
  return (
    <Modal
      className={`modal-compare ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      visible={visible}
      footer={null}
      onCancel={onCancel}
      wrapClassName="modal-compare__wrapper"
      width={modalWidth}
    >
      <div>
        <div className="activity-modal-compare__title-row">
          <h4 className="activity-modal-compare__title secondary-font">
            <FormattedMessage id="results.activity.card.compare.modal.title" />
          </h4>
        </div>

        <div className="activity-modal-compare__hotels activity-modal-compare__hotels--meta">
          <div className="hotels-dates">
            {formatDateCheckInOut(startDate, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)} 
          </div>
          <div className="hotels-dates">
             {adults}{' '}
            <FormattedMessage id="adult" values={{ count: adults }} /> - {childs}{' '}
            <FormattedMessage id="child" values={{ count: childs }} />
          </div>
        </div>

        <div className="activity-modal-compare__hotels">
          {activities.map((activity, index) => {
            const  price  = activity.modalities[0].rates[0].price;

            const value = marginatorStore.value || 0;
            const marginatorPrice = price / (1 - value / 100);

            const publicPrice =
              isB2C || !activity.modalities[0].rates[0].publicPrices?.length ? null : Math.min(...activity.modalities[0].rates[0].publicPrices.map((p) => p.price));

            const msave = publicPrice ? (marginatorPrice * percents) / publicPrice : 100;
            const savings = Math.floor(percents - msave);

            return (
              <div key={index} className="activity-modal-compare__hotel">
                <div className="activity-modal-compare__media">
                  <div className="hotel-images-container">
                    <div className="activity-image" style={{ backgroundImage: `url(${get(activity, 'content.mediaImages[0].resource') || get(activity, 'images[0].url')})` }} />
                    {savings > 0 ? (
                      <div className="activity-modal-compare__savings-badge">
                        {savings.toFixed(0)} % <FormattedMessage id="results.activity.card.compare.modal.off" />
                      </div>
                    ) : null}
                  </div>
                  <label
                    className={`activity-modal-compare__remove-btn ${activities.length === 2 ? 'disabled' : ''}`}
                    onClick={() => {
                      if (activities.length > 2) {
                        onRemove(activity);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleXmark} className="activity-modal-compare__remove-btn-icon" />
                  </label>
                </div>

                <div className="hotel-text-container hotel-text-container--title">
                  <p title={activity.name}>{activity.name}</p>
                </div>
                <div className="hotel-text-container hotel-text-container--refundable">
                  <div className="activity-card-wrapper__refundable">
                    <span className={activity.modalities[0].rates?.[0]?.refundable ? 'hotel-card-wrapper__refundable-ref' : 'hotel-card-wrapper__refundable-nonref'}>
                      {activity.modalities[0].rates?.[0]?.refundable ? (
                        <FormattedMessage id="refundable" />
                      ) : (
                        <FormattedMessage id="non.refundable" />
                      )}
                    </span>
                  </div>
                </div>
      

                <div className="hotel-text-container hotel-text-container--rating">
                  {activity.reviews && activity.reviews.total > 0 ? (
                    <div className="trust-you-info">
                      <div className="hotel-stars">
                        <HotelStars stars={activity.reviews.averageRating / 2} />
                      </div>
                      <p className="trust-you-text">
                        <strong>{renderNumber(activity.reviews.averageRating, 1)}</strong>
                        /10 ({activity.reviews.total} <FormattedMessage id="reviews" />)
                      </p>
                    </div>
                  ) : (
                    <p>0.0/10</p>
                  )}
                </div>

                <div className="hotel-amenities-container hotel-amenities-container--meta">
                  {activity.modalities?.[0] ? (
                    <>
                      <div className="amenity-item">
                        <FormattedMessage id="activity.details.duration" />: {activity.modalities[0].durationValue} {activity.modalities[0].durationMetric}
                      </div>
                    </>
                  ) : (
                   null
                  )}
                </div>

                <div className="hotel-text-container hotel-text-container--price">
                  {publicPrice && savings > 0 ? (
                    <p style={{ textDecoration: 'line-through' }}>
                      <Currency currency={activity.currency} />
                      {renderNumber(publicPrice, 2)}
                    </p>
                  ) : null}
                  <p>
                    <strong>
                      <Currency currency={activity.currency} />
                      {renderNumber(marginatorPrice, 2)}
                    </strong>
                  </p>
                </div>

       

                <div className="hotel-view-container">
                  <div className="view-button" onClick={() => handleView(activity)}>
                    <FormattedMessage id="results.activity.card.compare.modal.view" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="activity-modal-compare__buttons">
          {!account || account.type !== LoginType.PrivateWithToken ? (
            account?.displayShareCompareActivity && (
              <BlueButton disabled={loading} onClick={handleDownload}>
                {loading ? <Spin /> : <FormattedMessage id="results.activity.card.compare.modal.download" />}
              </BlueButton>
            )
          ) : (
            <>
              {account?.displayShareCompareActivity && (
                <BlueButton
                  disabled={loading}
                  onClick={() => {
                    setVisibleAction(true);
                    setIsShare(false);
                  }}
                >
                  {loading ? <Spin /> : <FormattedMessage id="results.activity.card.compare.modal.download" />}
                </BlueButton>
              )}

              {HasPermission(user, ViewQuotesSharePermission) ? (
                <BlueButton
                  disabled={loading}
                  onClick={() => {
                    setVisibleAction(true);
                    setIsShare(true);
                  }}
                >
                  {loading ? <Spin /> : <FormattedMessage id="results.activity.card.compare.modal.share" />}
                </BlueButton>
              ) : null}
            </>
          )}
        </div>
      </div>
      <ModalShare
        visible={visibleAction}
        isShare={isShare}
        type={DownLoadShareType.ACTIVITY}
        onCancel={() => setVisibleAction(false)}
        onLoading={(v) => setLoading(v)}
        convertData={handleData}
      />
    </Modal>
  );
}
